import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import { DemoProfile, Usertype } from './types';

import { DOCUMENT_TYPE_INFO } from 'modules/Onboarding/BusinessVerificationFlow/constants';
import CheckCircleIcon from 'modules/common/components/Icons/CheckCircleIcon';
import CloudUploadIcon from 'modules/common/components/Icons/CloudUploadIcon';
import DocumentCheckIcon from 'modules/common/components/Icons/DocumentCheckIcon';

/**
 * Demo-only business verification (replaces the real flow at /onboarding/business-verification).
 * Clean upload UI + instant advance — no AI pipeline, interstitials, or flicker. AOO/Middesk omitted.
 */

type DocType = keyof typeof DOCUMENT_TYPE_INFO;

const STEPS_BY_JOURNEY: Partial<Record<Usertype, DocType[]>> = {
    'SC-SC': ['EIN_CONFIRMATION_DOCUMENT', 'S_CORP_ACCEPTANCE_LETTER'],
    'LLC-SC': ['EIN_CONFIRMATION_DOCUMENT'],
    'LLC-LLC': ['EIN_CONFIRMATION_DOCUMENT'],
};

function UploadDropzone({ uploaded, onUpload }: Readonly<{ uploaded: boolean; onUpload: () => void }>) {
    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={onUpload}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onUpload();
                }
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 5,
                cursor: 'pointer',
                border: '1px dashed',
                borderColor: uploaded ? 'success.main' : 'cement.400',
                borderRadius: 2,
                backgroundColor: 'cement.50',
            }}>
            {uploaded ? (
                <>
                    <DocumentCheckIcon sx={{ color: 'success.main' }} fontSize="large" />
                    <Typography variant="subtitle1">document.pdf</Typography>
                    <Typography variant="caption" color="success.main">
                        Uploaded
                    </Typography>
                </>
            ) : (
                <>
                    <CloudUploadIcon sx={{ color: 'text.secondary' }} fontSize="large" />
                    <Typography variant="subtitle1">Drag and drop or click to upload</Typography>
                    <Typography variant="caption" color="text.secondary">
                        PDF, JPG, or PNG
                    </Typography>
                </>
            )}
        </Box>
    );
}

function VerificationSuccess({ onFinish }: Readonly<{ onFinish: () => void }>) {
    return (
        <Stack spacing={3} alignItems="center" textAlign="center" sx={{ maxWidth: 520 }}>
            <CheckCircleIcon sx={{ color: 'success.main', width: 56, height: 56 }} />
            <Typography variant="h2">We&apos;re reviewing your documents</Typography>
            <Typography variant="body1" color="text.secondary">
                This usually takes 1–2 business days. Look out for messages in your Collective dashboard and email about
                any requested information and next steps.
            </Typography>
            <Button variant="contained" color="primary" size="large" onClick={onFinish}>
                Finish
            </Button>
        </Stack>
    );
}

export default function DemoBusinessVerification({ profile }: Readonly<{ profile: DemoProfile }>) {
    const history = useHistory();
    const steps = STEPS_BY_JOURNEY[profile.journey] ?? ['EIN_CONFIRMATION_DOCUMENT'];
    const [stepIndex, setStepIndex] = useState(0);
    const [uploaded, setUploaded] = useState(false);

    const handleContinue = () => {
        setUploaded(false);
        setStepIndex((index) => index + 1);
    };

    const isDone = stepIndex >= steps.length;
    const docInfo = isDone ? null : DOCUMENT_TYPE_INFO[steps[stepIndex]];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'cement.50',
                px: 3,
            }}>
            {docInfo ? (
                <Paper variant="outlined" sx={{ p: 5, width: '100%', maxWidth: 640 }}>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                                {`Step ${stepIndex + 1} of ${steps.length}`}
                            </Typography>
                            <Typography variant="h2">{`Upload your ${docInfo.label}`}</Typography>
                            <Typography variant="body1" color="text.secondary">
                                {docInfo.description}
                            </Typography>
                        </Stack>
                        <UploadDropzone uploaded={uploaded} onUpload={() => setUploaded(true)} />
                        <Button variant="contained" color="primary" size="large" onClick={handleContinue}>
                            Continue
                        </Button>
                    </Stack>
                </Paper>
            ) : (
                <VerificationSuccess onFinish={() => history.push('/dashboard')} />
            )}
        </Box>
    );
}
